"use client";

import React, { useCallback, useRef, useState } from "react";
import Image from "next/image";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import Button from "@/components/ui/Button";
import FloatingSelect from "@/components/ui/FloatingSelect";
import axiosInstance, { endpoints } from "@/lib/axios";

// ─── Types ────────────────────────────────────────────────────────────────────

type TypeClient = "particulier" | "entreprise";

interface CareerFormData {
  typeClient: TypeClient;
  entreprise: string;
  siret: string;
  nom: string;
  prenom: string;
  email: string;
  telephone: string;
  fonctionInterlocuteur: string;
  detailDemande: string;
  nombrePersonnes: string;
  financement: string;
  financementOpco: string;
  financementAutre: string;
  format: string;
  typeFormation: string;
  delai: string;
  lieuSouhaite: string;
}

type FieldErrors = Partial<Record<keyof CareerFormData, string>>;

interface StepProps {
  data: CareerFormData;
  errors: FieldErrors;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onSelectChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  /** Particulier / Entreprise switcher — used by step 1 only. */
  onTypeClientChange: (value: TypeClient) => void;
  /** Optional "cahier des charges" attachment — used by step 3 only. */
  cahierDesCharges: File | null;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const TOTAL_STEPS = 6;

const STEP_TITLES: Record<number, string> = {
  1: "Informations de contact",
  2: "Détails du contact",
  3: "Demande de formation",
  4: "Financement",
  5: "Modalités de formation",
  6: "Planification",
};

const TYPE_CLIENT_OPTIONS: { value: TypeClient; label: string }[] = [
  { value: "particulier", label: "Particulier" },
  { value: "entreprise", label: "Entreprise" },
];

const FONCTION_OPTIONS = [
  { value: "direction", label: "Direction" },
  { value: "rh", label: "RH" },
  { value: "responsable-formation", label: "Responsable Formation" },
  { value: "manager", label: "Manager" },
  { value: "autre", label: "Autre" },
];

const FINANCEMENT_OPTIONS = [
  { value: "entreprise", label: "Entreprise" },
  { value: "opco", label: "OPCO" },
  { value: "ne-sais-pas", label: "Je ne sais pas encore" },
  { value: "autre", label: "Autre" },
];

// Shown only when "OPCO" is selected above, to know which one.
const OPCO_OPTIONS = [
  { value: "opco-atlas", label: "OPCO Atlas" },
  { value: "opco-akto", label: "OPCO AKTO" },
  { value: "opco-mobilites", label: "OPCO Mobilités" },
  { value: "opco-sante", label: "OPCO Santé" },
  { value: "opco-ep", label: "OPCO EP" },
  { value: "opco-constructys", label: "OPCO Constructys" },
  { value: "opco-ocapiat", label: "OPCO OCAPIAT" },
  { value: "opco-uniformation", label: "OPCO Uniformation" },
  { value: "opco-afdas", label: "OPCO AFDAS" },
  { value: "opco-opcommerce", label: "OPCO Opcommerce" },
  { value: "opco-cohesion-sociale", label: "OPCO Cohésion Sociale" },
  { value: "opco-autre", label: "Autre OPCO" },
];

const FORMAT_OPTIONS = [
  { value: "presentiel", label: "Présentiel" },
  { value: "distanciel", label: "Distanciel (Visio)" },
  { value: "pas-de-preference", label: "Pas de préférence" },
];

const TYPE_FORMATION_OPTIONS = [
  { value: "inter", label: "Inter" },
  { value: "intra", label: "Intra" },
  { value: "sur-mesure", label: "Sur-mesure" },
  { value: "ne-sais-pas", label: "Je ne sais pas" },
];

const DELAI_OPTIONS = [
  { value: "dans-le-mois", label: "Dans le mois" },
  { value: "dans-3-mois", label: "Dans les 3 mois" },
  { value: "dans-6-mois", label: "Dans les 6 mois" },
  { value: "dans-l-annee", label: "Dans l’année" },
  { value: "projet", label: "C’est seulement un projet" },
];

// Same extensions as the mentor application form (Enseigner/ApplicationFormSection).
const ATTACHMENT_ACCEPT = ".doc,.docx,.pdf,.rtf,.txt";
const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024; // 10 Mo

const INITIAL_FORM_DATA: CareerFormData = {
  typeClient: "entreprise",
  entreprise: "",
  siret: "",
  nom: "",
  prenom: "",
  email: "",
  telephone: "",
  fonctionInterlocuteur: "",
  detailDemande: "",
  nombrePersonnes: "",
  financement: "",
  financementOpco: "",
  financementAutre: "",
  format: "",
  typeFormation: "",
  delai: "",
  lieuSouhaite: "",
};

// ─── Shared styles ────────────────────────────────────────────────────────────

const INPUT_BASE =
  "w-full px-3 py-2.5 bg-white border rounded-lg text-[#1D1D1D] text-xs placeholder:text-[#ABABAB] focus:outline-none focus:ring-0 h-10 transition-colors";

const INPUT_CLASS = `${INPUT_BASE} border-[#EFEFEF] focus:border-[#40BFDE]`;
const INPUT_ERROR_CLASS = `${INPUT_BASE} border-red-400 focus:border-red-500`;

const TEXTAREA_BASE =
  "w-full bg-white border rounded-lg px-3 py-2.5 text-[#1D1D1D] text-xs leading-5 placeholder:text-[#ABABAB] focus:outline-none focus:ring-0 min-h-[132px] resize-none transition-colors";

const TEXTAREA_CLASS = `${TEXTAREA_BASE} border-[#EFEFEF] focus:border-[#40BFDE]`;
const TEXTAREA_ERROR_CLASS = `${TEXTAREA_BASE} border-red-400 focus:border-red-500`;

const LABEL_CLASS = "block text-xs font-medium text-[#1B365E] mb-1";
const ERROR_CLASS = "text-[10px] text-red-500 mt-1";

// ─── Validation ───────────────────────────────────────────────────────────────

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validateStep(step: number, data: CareerFormData): FieldErrors {
  const errors: FieldErrors = {};

  switch (step) {
    case 1:
      if (data.typeClient === "entreprise" && !data.entreprise.trim())
        errors.entreprise = "Ce champ est obligatoire";
      if (!data.nom.trim()) errors.nom = "Ce champ est obligatoire";
      if (!data.prenom.trim()) errors.prenom = "Ce champ est obligatoire";
      if (!data.email.trim()) errors.email = "Ce champ est obligatoire";
      else if (!EMAIL_REGEX.test(data.email)) errors.email = "Adresse email invalide";
      if (!data.telephone.trim()) errors.telephone = "Ce champ est obligatoire";
      break;

    case 3:
      if (!data.detailDemande.trim()) errors.detailDemande = "Ce champ est obligatoire";
      if (!data.nombrePersonnes.trim()) errors.nombrePersonnes = "Ce champ est obligatoire";
      else if (Number(data.nombrePersonnes) < 1) errors.nombrePersonnes = "Le nombre doit être au moins 1";
      break;

    case 4:
      if (!data.financement) errors.financement = "Ce champ est obligatoire";
      if (data.financement === "opco" && !data.financementOpco)
        errors.financementOpco = "Veuillez préciser votre OPCO";
      if (data.financement === "autre" && !data.financementAutre.trim())
        errors.financementAutre = "Veuillez préciser votre financement";
      break;

    case 5:
      if (!data.format) errors.format = "Ce champ est obligatoire";
      if (!data.typeFormation) errors.typeFormation = "Ce champ est obligatoire";
      break;

    default:
      break;
  }

  return errors;
}

// ─── Field wrapper ────────────────────────────────────────────────────────────

interface FieldProps {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}

function Field({ id, label, required, error, children }: FieldProps) {
  return (
    <div>
      <label htmlFor={id} className={LABEL_CLASS}>
        {label}
        {required && (
          <span className="text-red-400 ml-0.5" aria-hidden="true">
            {" *"}
          </span>
        )}
      </label>
      {children}
      {error && (
        <p id={`${id}-error`} role="alert" aria-live="polite" className={ERROR_CLASS}>
          {error}
        </p>
      )}
    </div>
  );
}

// ─── Step indicator ───────────────────────────────────────────────────────────

function StepIndicator({ currentStep }: { currentStep: number }) {
  const progress = Math.round((currentStep / TOTAL_STEPS) * 100);

  return (
    <div className="mb-5">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-[#1B365E]">
          Étape {currentStep}/{TOTAL_STEPS}
        </p>
        <p className="text-xs text-[#ABABAB]">{progress}%</p>
      </div>

      <div
        className="w-full bg-[#EFEFEF] rounded-full h-1.5 mb-2"
        role="progressbar"
        aria-valuenow={currentStep}
        aria-valuemin={1}
        aria-valuemax={TOTAL_STEPS}
        aria-label={`Étape ${currentStep} sur ${TOTAL_STEPS}`}
      >
        <div
          className="bg-[#40BFDE] h-1.5 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      <p className="text-[10px] text-[#ABABAB] text-center mb-2">{STEP_TITLES[currentStep]}</p>

      <div className="flex gap-1.5 justify-center" aria-hidden="true">
        {Array.from({ length: TOTAL_STEPS }, (_, i) => {
          const s = i + 1;
          return (
            <span
              key={s}
              className={`rounded-full transition-all duration-300 ${
                s === currentStep
                  ? "h-2 w-4 bg-[#40BFDE]"
                  : s < currentStep
                  ? "h-2 w-2 bg-[#1B365E]"
                  : "h-2 w-2 bg-[#D9D9D9]"
              }`}
            />
          );
        })}
      </div>
    </div>
  );
}

// ─── Steps ────────────────────────────────────────────────────────────────────

function Step1({ data, errors, onInputChange, onTypeClientChange }: StepProps) {
  return (
    <div className="space-y-3.5">
      <div>
        <p className={LABEL_CLASS}>Vous êtes</p>
        <div role="radiogroup" aria-label="Vous êtes" className="grid grid-cols-2 gap-3">
          {TYPE_CLIENT_OPTIONS.map((option) => {
            const isChecked = data.typeClient === option.value;
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={isChecked}
                onClick={() => onTypeClientChange(option.value)}
                className={`h-10 rounded-lg border text-xs cursor-pointer transition-colors ${
                  isChecked
                    ? "border-[#40BFDE] bg-[#40BFDE]/10 font-medium text-[#40BFDE]"
                    : "border-[#EFEFEF] bg-white text-[#1D1D1D] hover:border-[#40BFDE]"
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      <AnimatePresence initial={false}>
        {data.typeClient === "entreprise" && (
          <motion.div
            key="entrepriseFields"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            style={{ overflow: "hidden" }}
            className="space-y-3.5"
          >
            <Field id="entreprise" label="Entreprise" required error={errors.entreprise}>
              <input
                id="entreprise"
                name="entreprise"
                value={data.entreprise}
                onChange={onInputChange}
                placeholder="Nom de votre entreprise"
                aria-required="true"
                aria-invalid={!!errors.entreprise}
                aria-describedby={errors.entreprise ? "entreprise-error" : undefined}
                className={errors.entreprise ? INPUT_ERROR_CLASS : INPUT_CLASS}
              />
            </Field>

            <Field id="siret" label="SIRET">
              <input
                id="siret"
                name="siret"
                value={data.siret}
                onChange={onInputChange}
                placeholder="123 456 789 00000"
                className={INPUT_CLASS}
              />
            </Field>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-2 gap-3">
        <Field id="nom" label="Nom" required error={errors.nom}>
          <input
            id="nom"
            name="nom"
            value={data.nom}
            onChange={onInputChange}
            placeholder="Dupont"
            aria-required="true"
            aria-invalid={!!errors.nom}
            aria-describedby={errors.nom ? "nom-error" : undefined}
            className={errors.nom ? INPUT_ERROR_CLASS : INPUT_CLASS}
          />
        </Field>

        <Field id="prenom" label="Prénom" required error={errors.prenom}>
          <input
            id="prenom"
            name="prenom"
            value={data.prenom}
            onChange={onInputChange}
            placeholder="Marie"
            aria-required="true"
            aria-invalid={!!errors.prenom}
            aria-describedby={errors.prenom ? "prenom-error" : undefined}
            className={errors.prenom ? INPUT_ERROR_CLASS : INPUT_CLASS}
          />
        </Field>
      </div>

      <Field id="email" label="Email" required error={errors.email}>
        <input
          id="email"
          name="email"
          type="email"
          value={data.email}
          onChange={onInputChange}
          placeholder="marie.dupont@entreprise.fr"
          aria-required="true"
          aria-invalid={!!errors.email}
          aria-describedby={errors.email ? "email-error" : undefined}
          className={errors.email ? INPUT_ERROR_CLASS : INPUT_CLASS}
        />
      </Field>

      <Field id="telephone" label="Téléphone" required error={errors.telephone}>
        <div className="flex gap-2.5">
          <div className="w-14 h-10 px-3 flex items-center justify-center rounded-lg border border-[#EFEFEF] bg-white text-[12px] text-[#ABABAB] shrink-0">
            +33
          </div>
          <input
            id="telephone"
            name="telephone"
            type="tel"
            value={data.telephone}
            onChange={onInputChange}
            placeholder="06 12 34 56 78"
            aria-required="true"
            aria-invalid={!!errors.telephone}
            aria-describedby={errors.telephone ? "telephone-error" : undefined}
            className={errors.telephone ? INPUT_ERROR_CLASS : INPUT_CLASS}
          />
        </div>
      </Field>
    </div>
  );
}

function Step2({ data, onSelectChange }: StepProps) {
  return (
    <div className="space-y-3.5">
      <p className="text-xs text-[#ABABAB]">Cette étape est optionnelle.</p>
      <FloatingSelect
        label="Fonction de l'interlocuteur"
        name="fonctionInterlocuteur"
        value={data.fonctionInterlocuteur}
        onChange={onSelectChange}
        options={FONCTION_OPTIONS}
        placeholder="Sélectionnez une fonction"
        lessGap
      />
    </div>
  );
}

function Step3({ data, errors, onInputChange, cahierDesCharges, onFileChange }: StepProps) {
  return (
    <div className="space-y-3.5">
      <Field id="detailDemande" label="Détail de la demande" required error={errors.detailDemande}>
        <textarea
          id="detailDemande"
          name="detailDemande"
          value={data.detailDemande}
          onChange={onInputChange}
          placeholder="Décrivez votre besoin de formation en détail..."
          aria-required="true"
          aria-invalid={!!errors.detailDemande}
          aria-describedby={errors.detailDemande ? "detailDemande-error" : undefined}
          className={errors.detailDemande ? TEXTAREA_ERROR_CLASS : TEXTAREA_CLASS}
        />
      </Field>

      <Field id="nombrePersonnes" label="Nombre de personnes à former" required error={errors.nombrePersonnes}>
        <input
          id="nombrePersonnes"
          name="nombrePersonnes"
          type="number"
          min="1"
          value={data.nombrePersonnes}
          onChange={onInputChange}
          placeholder="Ex : 10"
          aria-required="true"
          aria-invalid={!!errors.nombrePersonnes}
          aria-describedby={errors.nombrePersonnes ? "nombrePersonnes-error" : undefined}
          className={errors.nombrePersonnes ? INPUT_ERROR_CLASS : INPUT_CLASS}
        />
      </Field>

      <Field id="cahierDesCharges" label="Cahier des charges (facultatif)">
        <input
          id="cahierDesCharges"
          name="cahierDesCharges"
          type="file"
          accept={ATTACHMENT_ACCEPT}
          onChange={onFileChange}
          className="peer sr-only"
        />
        <label
          htmlFor="cahierDesCharges"
          className={`${INPUT_CLASS} flex items-center justify-between gap-2 cursor-pointer peer-focus-visible:border-[#40BFDE]`}
        >
          <span className="flex items-center gap-2 min-w-0">
            <Image
              src="/assets/icons/paperclip.svg"
              alt=""
              aria-hidden="true"
              width={14}
              height={16}
              className="shrink-0"
            />
            <span className={`truncate ${cahierDesCharges ? "font-medium" : "text-[#ABABAB]"}`}>
              {cahierDesCharges ? cahierDesCharges.name : "Joindre un document"}
            </span>
          </span>
          {!cahierDesCharges && (
            <span className="hidden sm:block shrink-0 text-[10px] text-[#9CA3AF] whitespace-nowrap">
              .doc .docx .pdf .rtf .txt
            </span>
          )}
        </label>
      </Field>
    </div>
  );
}

function Step4({ data, errors, onInputChange, onSelectChange }: StepProps) {
  return (
    <div className="space-y-3.5">
      <div>
        <FloatingSelect
          label="Financement *"
          name="financement"
          value={data.financement}
          onChange={onSelectChange}
          options={FINANCEMENT_OPTIONS}
          placeholder="Sélectionnez votre financement"
          lessGap
        />
        {errors.financement && (
          <p role="alert" aria-live="polite" className={ERROR_CLASS}>
            {errors.financement}
          </p>
        )}
      </div>

      <AnimatePresence>
        {data.financement === "opco" && (
          <motion.div
            key="financementOpco"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            style={{ overflow: "hidden" }}
          >
            <FloatingSelect
              label="Précisez votre OPCO *"
              name="financementOpco"
              value={data.financementOpco}
              onChange={onSelectChange}
              options={OPCO_OPTIONS}
              placeholder="Sélectionnez votre OPCO"
              lessGap
            />
            {errors.financementOpco && (
              <p role="alert" aria-live="polite" className={ERROR_CLASS}>
                {errors.financementOpco}
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {data.financement === "autre" && (
          <motion.div
            key="financementAutre"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            style={{ overflow: "hidden" }}
          >
            <Field
              id="financementAutre"
              label="Précisez votre financement"
              required
              error={errors.financementAutre}
            >
              <input
                id="financementAutre"
                name="financementAutre"
                value={data.financementAutre}
                onChange={onInputChange}
                placeholder="Précisez votre financement..."
                aria-required="true"
                aria-invalid={!!errors.financementAutre}
                aria-describedby={errors.financementAutre ? "financementAutre-error" : undefined}
                className={errors.financementAutre ? INPUT_ERROR_CLASS : INPUT_CLASS}
              />
            </Field>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Step5({ data, errors, onSelectChange }: StepProps) {
  return (
    <div className="space-y-3.5">
      <div>
        <FloatingSelect
          label="Format *"
          name="format"
          value={data.format}
          onChange={onSelectChange}
          options={FORMAT_OPTIONS}
          placeholder="Sélectionnez un format"
          lessGap
        />
        {errors.format && (
          <p role="alert" aria-live="polite" className={ERROR_CLASS}>
            {errors.format}
          </p>
        )}
      </div>

      <div>
        <FloatingSelect
          label="Type de formation *"
          name="typeFormation"
          value={data.typeFormation}
          onChange={onSelectChange}
          options={TYPE_FORMATION_OPTIONS}
          placeholder="Sélectionnez un type"
          lessGap
        />
        {errors.typeFormation && (
          <p role="alert" aria-live="polite" className={ERROR_CLASS}>
            {errors.typeFormation}
          </p>
        )}
      </div>
    </div>
  );
}

function Step6({ data, onInputChange, onSelectChange }: StepProps) {
  return (
    <div className="space-y-3.5">
      <p className="text-xs text-[#ABABAB]">Ces informations sont optionnelles.</p>

      <FloatingSelect
        label="Délai"
        name="delai"
        value={data.delai}
        onChange={onSelectChange}
        options={DELAI_OPTIONS}
        placeholder="Sélectionnez un délai"
        lessGap
      />

      {/* Le lieu n'a pas de sens pour une formation en visio. */}
      {data.format !== "distanciel" && (
        <Field id="lieuSouhaite" label="Lieu souhaité">
          <input
            id="lieuSouhaite"
            name="lieuSouhaite"
            value={data.lieuSouhaite}
            onChange={onInputChange}
            placeholder="Paris, Lyon, en ligne..."
            className={INPUT_CLASS}
          />
        </Field>
      )}
    </div>
  );
}

// ─── Step registry ────────────────────────────────────────────────────────────

const STEP_COMPONENTS: Record<number, React.ComponentType<StepProps>> = {
  1: Step1,
  2: Step2,
  3: Step3,
  4: Step4,
  5: Step5,
  6: Step6,
};

// ─── Success state ────────────────────────────────────────────────────────────

function SuccessMessage() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className="text-center py-6"
    >
      <div className="w-12 h-12 rounded-full bg-[#40BFDE]/20 flex items-center justify-center mx-auto mb-4">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M5 13l4 4L19 7"
            stroke="#40BFDE"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <h3 className="text-[#1B365E] font-red-hat text-[20px] font-semibold mb-2">
        Demande envoyée !
      </h3>
      <p className="text-[#1B365E] text-xs leading-5 max-w-[280px] mx-auto">
        Nous avons bien reçu votre dossier. Notre équipe vous contactera très prochainement.
      </p>
    </motion.div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function CareerGuidanceForm() {
  const [currentStep, setCurrentStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [formData, setFormData] = useState<CareerFormData>(INITIAL_FORM_DATA);
  const [cahierDesCharges, setCahierDesCharges] = useState<File | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const clearFieldError = useCallback((field: keyof CareerFormData) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      clearFieldError(name as keyof CareerFormData);
    },
    [clearFieldError]
  );

  const onSelectChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      clearFieldError(name as keyof CareerFormData);
    },
    [clearFieldError]
  );

  const onTypeClientChange = useCallback(
    (value: TypeClient) => {
      setFormData((prev) => ({ ...prev, typeClient: value }));
      // The company fields are hidden for a particulier — drop their stale error.
      clearFieldError("entreprise");
    },
    [clearFieldError]
  );

  const onFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;

    if (file && file.size > MAX_ATTACHMENT_SIZE) {
      toast.error("Ce fichier est trop volumineux (10 Mo maximum).");
      e.target.value = "";
      setCahierDesCharges(null);
      return;
    }

    setCahierDesCharges(file);
  }, []);

  const scrollToForm = () => {
    containerRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  const goNext = () => {
    const stepErrors = validateStep(currentStep, formData);

    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      const firstKey = Object.keys(stepErrors)[0];
      document.getElementById(firstKey)?.focus();
      return;
    }

    setErrors({});

    if (currentStep === TOTAL_STEPS) {
      handleSubmit();
      return;
    }

    setDirection(1);
    setCurrentStep((s) => s + 1);
    scrollToForm();
  };

  const goPrev = () => {
    setErrors({});
    setDirection(-1);
    setCurrentStep((s) => Math.max(1, s - 1));
    scrollToForm();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const financementBaseLabel =
        FINANCEMENT_OPTIONS.find((o) => o.value === formData.financement)?.label ??
        formData.financement;
      const financementLabel =
        formData.financement === "autre"
          ? formData.financementAutre
          : formData.financement === "opco"
          ? OPCO_OPTIONS.find((o) => o.value === formData.financementOpco)?.label ??
            financementBaseLabel
          : financementBaseLabel;
      const formatLabel =
        FORMAT_OPTIONS.find((o) => o.value === formData.format)?.label ?? formData.format;
      const typeFormationLabel =
        TYPE_FORMATION_OPTIONS.find((o) => o.value === formData.typeFormation)?.label ??
        formData.typeFormation;
      const fonctionLabel = FONCTION_OPTIONS.find(
        (o) => o.value === formData.fonctionInterlocuteur
      )?.label;
      const delaiLabel = DELAI_OPTIONS.find((o) => o.value === formData.delai)?.label;

      const isEntreprise = formData.typeClient === "entreprise";

      const message = [
        `Détail de la demande : ${formData.detailDemande}`,
        `Profil : ${isEntreprise ? "Entreprise" : "Particulier"}`,
        isEntreprise && `Entreprise : ${formData.entreprise}`,
        isEntreprise && formData.siret.trim() && `SIRET : ${formData.siret}`,
        fonctionLabel && `Fonction de l'interlocuteur : ${fonctionLabel}`,
        `Nombre de personnes à former : ${formData.nombrePersonnes}`,
        `Financement : ${financementLabel}`,
        `Format : ${formatLabel}`,
        `Type de formation : ${typeFormationLabel}`,
        delaiLabel && `Délai : ${delaiLabel}`,
        formData.format !== "distanciel" &&
          formData.lieuSouhaite.trim() &&
          `Lieu souhaité : ${formData.lieuSouhaite}`,
      ]
        .filter(Boolean)
        .join("\n");

      const payload: Record<string, string> = {
        first_name: formData.prenom,
        last_name: formData.nom,
        email: formData.email,
        phone: `+33${formData.telephone}`,
        message,
        origin: "form",
      };
      if (typeFormationLabel) payload.formation_theme = typeFormationLabel;

      if (cahierDesCharges) {
        // The attachment forces multipart — the axios instance defaults to JSON,
        // which would otherwise serialize the FormData and drop the file.
        const body = new FormData();
        Object.entries(payload).forEach(([key, value]) => body.append(key, value));
        body.append("attachment", cahierDesCharges);

        await axiosInstance.post(endpoints.leads.create, body, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await axiosInstance.post(endpoints.leads.create, payload);
      }

      setSubmitted(true);
    } catch {
      toast.error("Une erreur est survenue lors de l'envoi. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) return <SuccessMessage />;

  const CurrentStep = STEP_COMPONENTS[currentStep];
  const isLastStep = currentStep === TOTAL_STEPS;

  return (
    <div ref={containerRef}>
      <StepIndicator currentStep={currentStep} />

      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: direction * 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: direction * -24 }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
        >
          <CurrentStep
            data={formData}
            errors={errors}
            onInputChange={onInputChange}
            onSelectChange={onSelectChange}
            onTypeClientChange={onTypeClientChange}
            cahierDesCharges={cahierDesCharges}
            onFileChange={onFileChange}
          />
        </motion.div>
      </AnimatePresence>

      <div className={`flex mt-5 gap-3 ${currentStep > 1 ? "justify-between" : "justify-end"}`}>
        {currentStep > 1 && (
          <Button variant="ghost" size="sm" onClick={goPrev} disabled={submitting} type="button">
            Précédent
          </Button>
        )}
        <Button
          size="sm"
          onClick={goNext}
          disabled={submitting}
          type="button"
          className="min-w-[140px]"
        >
          {submitting
            ? "Envoi en cours..."
            : isLastStep
            ? "Envoyer ma demande"
            : "Suivant"}
        </Button>
      </div>

      <p className="text-[10px] text-[#1B365E] mt-3 font-medium tracking-[0.25px]">
        *Les champs marqués d’un astérisque sont obligatoires
      </p>
    </div>
  );
}
